/**
 * Adapters: converte tipos do BE (BeAuction, BeArrematacao, etc.)
 * para os tipos internos do FE (AuctionItem, Arrematacao, etc.)
 */

import { opportunitiesApi, type BeAuction } from './auctions';
import type { BeArrematacao, BeCusto, BeVenda } from './arrematacoes';
import type { AuctionItem } from '@/app/components/AuctionDetailModal';
import { listScoreFromAuctionRow } from '@/lib/score-auction';
import { labelTipoImovelApi, labelModalidadeMer } from '@/lib/tipo-imovel-label';
import { resolveAuctionCountdown, formatAuctionDateTimeBR } from '@/lib/auction-countdown';

type Tier = 'Excelente' | 'Bom' | 'Regular' | 'Risco';
type Risk = 'Baixo' | 'Médio' | 'Alto' | 'Crítico';

/**
 * Converte texto bruto de data do BE para pt-BR no fuso de São Paulo.
 * Delega para parser que trata ISO date-only e dd/mm/yyyy sem shift UTC.
 */
export function formatDateTimeBR(isoStr?: string | null): string {
  return formatAuctionDateTimeBR(isoStr);
}

function normalizeTier(tier?: string | null): Tier {
  const t = tier?.toLowerCase() ?? '';
  if (t.includes('excel')) return 'Excelente';
  if (t.includes('bom') || t.includes('good')) return 'Bom';
  if (t.includes('risco') || t.includes('risk')) return 'Risco';
  return 'Regular';
}

function normalizeRisk(risk?: string | null): Risk {
  const r = risk?.toLowerCase() ?? '';
  if (r.includes('baixo') || r.includes('low')) return 'Baixo';
  if (r.includes('alto') || r.includes('high')) return 'Alto';
  if (r.includes('crít') || r.includes('crit')) return 'Crítico';
  return 'Médio';
}

/**
 * Converte um BeAuction (resposta do BE) para AuctionItem (formato do FE).
 * O campo `beId` guarda o UUID do banco para operações de sync.
 */
export function beAuctionToAuctionItem(
  a: BeAuction,
  opts?: { interestMarkedForExclusion?: boolean }
): AuctionItem & { beId: string } {
  // Trata desconto=0 como "não informado" — tenta calcular de valorMercado
  const descontoDb = a.desconto != null && a.desconto > 0 ? a.desconto : null;
  const valorMercadoDb = a.valorMercado && a.valorMercado > 0 ? a.valorMercado : null;

  const desconto =
    descontoDb ??
    (valorMercadoDb && valorMercadoDb > a.lanceMin
      ? Math.round(((valorMercadoDb - a.lanceMin) / valorMercadoDb) * 100)
      : 0);

  const valorMercado =
    valorMercadoDb ??
    (desconto > 0 && desconto < 100
      ? Math.round(a.lanceMin / (1 - desconto / 100))
      : a.lanceMin * 1.3);

  const roiEstimado =
    a.roiEstimado && a.roiEstimado > 0
      ? a.roiEstimado
      : desconto > 10
        ? parseFloat((desconto * 0.55).toFixed(1))
        : 0;

  const listScore = listScoreFromAuctionRow({
    desconto: a.desconto,
    valorMercado: a.valorMercado,
    lanceMin: a.lanceMin,
    tipoImovel: a.tipoImovel,
  });

  const countdown = resolveAuctionCountdown({
    firstRaw: a.dataLeilao,
    secondRaw: a.dataLeilao2,
    tipoVenda: a.tipoVenda,
  });

  return {
    id: `be-${a.id}`,
    beId: a.id,
    score: listScore,
    tier: normalizeTier(a.tier),
    tipo: labelTipoImovelApi(a.tipoImovel),
    endereco: a.endereco,
    cidade: a.cidade,
    estado: a.estado,
    cep: a.cep,
    lanceMin: a.lanceMin,
    valorMercado,
    valorLeilao2: a.valorLeilao2,
    desconto,
    roiEstimado,
    risco: normalizeRisk(a.risk),
    dataLeilao: formatDateTimeBR(a.dataLeilao),
    dataLeilao2: formatDateTimeBR(a.dataLeilao2) || undefined,
    dataLeilaoAtiva: countdown?.activeDateTimeBR || countdown?.activeDateBR || undefined,
    diasRestantes: countdown?.days ?? null,
    aiAnalisado: true,
    editalUrl: a.editalUrl,
    detailUrl: a.detailUrl,
    sourceName: a.sourceName,
    sourceId: a.sourceId,
    firstDiscoveredVia: a.firstDiscoveredVia ?? undefined,
    listingConflict: a.listingConflict ?? undefined,
    alerts: a.alerts ?? undefined,
    // Campos enriquecidos
    areaTotal: a.areaTotal,
    areaPrivativa: a.areaPrivativa,
    areaTerrano: a.areaTerrano,
    matricula: a.matricula,
    comarca: a.comarca,
    inscricaoImob: a.inscricaoImob,
    editalNumero: a.editalNumero,
    leiloeiro: a.leiloeiro,
    modalidade: a.modalidade != null ? labelModalidadeMer(String(a.modalidade)) : undefined,
    tipoVenda: a.tipoVenda,
    aceitaFgts: a.aceitaFgts,
    formasPagamento: a.formasPagamento,
    situacaoJuridica: a.situacaoJuridica,
    imageUrl: a.imageUrl,
    descricao: a.descricao,
    editalDocUrl: a.editalDocUrl,
    matriculaUrl: a.matriculaUrl,
    matriculaUrl2: a.matriculaUrl2,
    numeroLote: a.numeroLote ?? undefined,
    detalhesCarregados: a.detalhesCarregados,
    detalhesCarregadosEm: a.detalhesCarregadosEm ?? undefined,
    removidoDoCatalogoEm: a.removidoDoCatalogoEm ?? undefined,
    analiseIa:
      a.analiseIa ??
      (a as BeAuction & { aiAnalysis?: BeAuction['analiseIa'] }).aiAnalysis ??
      undefined,
    analiseIaEm:
      a.analiseIaEm ??
      (a as BeAuction & { aiAnalysisAt?: string | null }).aiAnalysisAt ??
      undefined,
    analiseVizinhanca: a.analiseVizinhanca ?? undefined,
    analiseVizinhancaEm: a.analiseVizinhancaEm ?? undefined,
    custosViabilidade:
      a.custosViabilidade ??
      (a as BeAuction & { viabilitySheet?: BeAuction['custosViabilidade'] }).viabilitySheet ??
      undefined,
    custosViabilidadeEm:
      a.custosViabilidadeEm ??
      (a as BeAuction & { viabilitySheetAt?: string | null }).viabilitySheetAt ??
      undefined,
    ...(opts?.interestMarkedForExclusion !== undefined
      ? { interestMarkedForExclusion: opts.interestMarkedForExclusion }
      : {}),
  };
}

/**
 * Converte lista paginada de BeAuctions para AuctionItems.
 */
export function beAuctionsToAuctionItems(
  auctions: BeAuction[]
): Array<AuctionItem & { beId: string }> {
  return auctions.map((a) => beAuctionToAuctionItem(a));
}

/**
 * Busca o leilão no BE pelo `beId` e mescla com o snapshot local (lista/favoritos).
 * Garante `analiseIa`, `analiseIaEm` e demais campos atualizados após salvar no banco.
 */
export async function refreshAuctionFromApi(auction: AuctionItem): Promise<AuctionItem> {
  const beId = (auction as AuctionItem & { beId?: string }).beId;
  if (!beId) return auction;
  try {
    const raw = await opportunitiesApi.getById(beId);
    return { ...auction, ...beAuctionToAuctionItem(raw) };
  } catch {
    return auction;
  }
}

// ─── Re-export para facilitar importação ────────────────────────────────────
export type { BeAuction, BeArrematacao, BeCusto, BeVenda };
